import React, { Component } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import AuthGate from './AuthGate';
import AccountHeader from './AccountHeader';
import '../static/css/SettingsPage.css';
import '../static/css/AffiliateDashboard.css';
import { trackScreen, SCREEN } from '../analytics';

const formatCents = (cents) => {
    if (cents == null) return '$0.00';
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

class AffiliateDashboard extends Component {
    constructor() {
        super();
        this.state = {
            loading: true,
            error: '',
            data: null,
            forbidden: false,
            copyFlash: '',
            showCashOutModal: false,
            cashOutMethod: 'venmo',
            cashOutHandle: '',
            isSubmittingCashOut: false,
            cashOutError: '',
        };
        this.handleCopy = this.handleCopy.bind(this);
        this.openCashOutModal = this.openCashOutModal.bind(this);
        this.closeCashOutModal = this.closeCashOutModal.bind(this);
        this.handleCashOutSubmit = this.handleCashOutSubmit.bind(this);
        this.fetchStatus = this.fetchStatus.bind(this);
    }

    componentDidMount() {
        // screen_view, deduped module-scope in analytics.js — do NOT add a local guard.
        // No-ops entirely when signed out.
        trackScreen(SCREEN.AFFILIATE_DASHBOARD);

        this.fetchStatus();
    }

    async fetchStatus() {
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ loading: false, error: 'Your session has expired. Please sign in again.' });
            return;
        }
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/affiliates/web-status',
                {},
                { headers: { Authorization: 'Bearer ' + token } },
            );
            this.setState({ loading: false, data: res.data.data, error: '' });
        } catch (err) {
            if (err.response && err.response.status === 403) {
                this.setState({ loading: false, forbidden: true });
                return;
            }
            this.setState({
                loading: false,
                error: 'We could not load your affiliate dashboard right now. Please try again in a moment.',
            });
        }
    }

    openCashOutModal() {
        this.setState({ showCashOutModal: true, cashOutError: '', cashOutHandle: '', cashOutMethod: 'venmo' });
    }

    closeCashOutModal() {
        if (this.state.isSubmittingCashOut) return;
        this.setState({ showCashOutModal: false });
    }

    async handleCashOutSubmit(event) {
        if (event && event.preventDefault) event.preventDefault();
        const { cashOutMethod, cashOutHandle } = this.state;
        const trimmedHandle = (cashOutHandle || '').trim();
        if (!trimmedHandle) {
            this.setState({ cashOutError: 'Please enter your payout handle, email, or phone number.' });
            return;
        }
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ cashOutError: 'Your session has expired. Please sign in again.' });
            return;
        }
        this.setState({ isSubmittingCashOut: true, cashOutError: '' });
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/affiliates/web-cashout',
                { method: cashOutMethod, handle: trimmedHandle },
                { headers: { Authorization: 'Bearer ' + token } },
            );
            const requestData = res.data && res.data.data ? res.data.data : {};
            const data = this.state.data;
            this.setState({
                isSubmittingCashOut: false,
                showCashOutModal: false,
                data: {
                    ...data,
                    kpis: { ...data.kpis, available_balance_cents: 0 },
                    pending_payout_request: {
                        amount_cents: requestData.amount_cents || 0,
                        requested_payout_method: requestData.requested_payout_method || cashOutMethod,
                        requested_payout_handle: requestData.requested_payout_handle || trimmedHandle,
                        requested_at: new Date().toISOString(),
                    },
                },
            });
        } catch (err) {
            const serverError = err && err.response && err.response.data && err.response.data.error;
            this.setState({
                isSubmittingCashOut: false,
                cashOutError: serverError || 'We could not submit your payout request. Please try again, or email hello@animates.app if it keeps happening.',
            });
        }
    }

    handleCopy() {
        const url = this.state.data && this.state.data.affiliate && this.state.data.affiliate.share_url;
        if (!url) return;
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(() => {
                this.setState({ copyFlash: 'Copied!' });
                setTimeout(() => this.setState({ copyFlash: '' }), 1800);
            }).catch(() => {
                this.setState({ copyFlash: 'Copy failed' });
                setTimeout(() => this.setState({ copyFlash: '' }), 1800);
            });
        }
    }

    renderTierLine(affiliate) {
        const isFounding = affiliate.tier === 'founding';
        const rateMonthly = affiliate.commission_rate_monthly;
        const rateYearly = affiliate.commission_rate_yearly;
        const sameRate = Number(rateMonthly) === Number(rateYearly);
        const rateLabel = sameRate
            ? `${Number(rateMonthly).toFixed(0)}% commission`
            : `${Number(rateMonthly).toFixed(0)}% monthly · ${Number(rateYearly).toFixed(0)}% yearly`;
        const windowLabel = isFounding
            ? 'Lifetime'
            : (affiliate.attribution_window_days
                ? `${Math.round(affiliate.attribution_window_days / 30)}-month window`
                : 'Lifetime');
        return (
            <div className="affiliate-tier-line">
                <span className={`affiliate-tier-badge affiliate-tier-badge-${isFounding ? 'founding' : 'regular'}`} style={{fontFamily: "'Chelsea Market', cursive"}}>
                    {isFounding ? '✨ Founding Affiliate ✨' : 'Affiliate'}
                </span>
                <span className="affiliate-tier-meta">{rateLabel} · {windowLabel}</span>
            </div>
        );
    }

    renderShareCard(affiliate) {
        const { copyFlash } = this.state;
        return (
            <div className="affiliate-card affiliate-share-card">
                {this.renderTierLine(affiliate)}
                <p className="affiliate-share-label">Your custom link</p>
                <div className="affiliate-share-row">
                    <code className="affiliate-share-url">{affiliate.share_url}</code>
                    <button
                        type="button"
                        className="affiliate-copy-button"
                        onClick={this.handleCopy}
                        aria-live="polite"
                    >
                        {copyFlash || 'Copy'}
                    </button>
                </div>
                <p className="affiliate-share-hint">
                    Your link uses your display name. If you change your display name in the app, this link updates automatically — but old links stop working.
                </p>
            </div>
        );
    }

    renderKpiTile(label, value, accent) {
        return (
            <div className={`affiliate-stat-card${accent ? ' affiliate-stat-card-accent' : ''}`}>
                <span className="affiliate-stat-label">{label}</span>
                <span className="affiliate-stat-value" style={{fontFamily: "'Chelsea Market', cursive"}}>{value}</span>
            </div>
        );
    }

    renderKpiGrid(kpis) {
        return (
            <div className="affiliate-stats-grid">
                {this.renderKpiTile('Clicks', kpis.clicks_total)}
                {this.renderKpiTile('Free Signups', kpis.free_signups)}
                {this.renderKpiTile('Paid Signups', kpis.paid_signups)}
                {this.renderKpiTile('Monthly Subs', kpis.active_monthly_subs)}
                {this.renderKpiTile('Yearly Subs', kpis.active_yearly_subs)}
                {this.renderKpiTile('YTD Earnings', formatCents(kpis.ytd_earnings_cents))}
                {this.renderKpiTile('This Month', formatCents(kpis.this_month_commission_cents))}
                {this.renderKpiTile('Available Balance', formatCents(kpis.available_balance_cents), true)}
            </div>
        );
    }

    renderCashOutSection(data) {
        const balance = data.kpis.available_balance_cents || 0;
        const minimum = data.min_payout_cents || 2500;
        const pending = data.pending_payout_request;

        if (pending) {
            const methodLabel = (pending.requested_payout_method || 'manual')
                .replace(/^[a-z]/, (c) => c.toUpperCase());
            return (
                <div className="affiliate-card affiliate-cashout-card affiliate-cashout-card-pending">
                    <span className="affiliate-cashout-pill">Payout pending</span>
                    <span className="affiliate-cashout-amount" style={{fontFamily: "'Chelsea Market', cursive"}}>
                        {formatCents(pending.amount_cents)}
                    </span>
                    <span className="affiliate-cashout-meta">
                        Requested {formatDate(pending.requested_at)} · {methodLabel} · {pending.requested_payout_handle}
                    </span>
                    <p className="affiliate-cashout-hint">
                        We're processing this manually — expect funds within a few business days.
                    </p>
                </div>
            );
        }

        const meetsMinimum = balance >= minimum;
        const shortfall = Math.max(minimum - balance, 0);

        return (
            <div className="affiliate-card affiliate-cashout-card">
                <span className="affiliate-cashout-label">Available to cash out</span>
                <span className="affiliate-cashout-amount" style={{fontFamily: "'Chelsea Market', cursive"}}>
                    {formatCents(balance)}
                </span>
                {meetsMinimum ? (
                    <span className="affiliate-cashout-meta">
                        Minimum payout {formatCents(minimum)} · paid manually via Venmo, PayPal, or Zelle
                    </span>
                ) : (
                    <span className="affiliate-cashout-meta">
                        Earn {formatCents(shortfall)} more to reach the {formatCents(minimum)} minimum.
                    </span>
                )}
                <button
                    type="button"
                    className="affiliate-cashout-cta"
                    onClick={this.openCashOutModal}
                    disabled={!meetsMinimum}
                >
                    Request Payout
                </button>
            </div>
        );
    }

    renderCashOutModal() {
        const { data, cashOutMethod, cashOutHandle, isSubmittingCashOut, cashOutError } = this.state;
        const balance = data.kpis.available_balance_cents || 0;
        const methods = [
            { id: 'venmo',  label: 'Venmo',  hint: 'e.g. @gustavo-arellano' },
            { id: 'paypal', label: 'PayPal', hint: 'e.g. you@example.com' },
            { id: 'zelle',  label: 'Zelle',  hint: 'email or phone' },
        ];
        const activeMethod = methods.find((m) => m.id === cashOutMethod) || methods[0];
        return (
            <div
                className="affiliate-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="affiliate-cashout-modal-title"
            >
                <form className="affiliate-modal-card" onSubmit={this.handleCashOutSubmit}>
                    <h3 id="affiliate-cashout-modal-title" className="affiliate-modal-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Request Payout</h3>
                    <p className="affiliate-modal-message">
                        You're cashing out <strong>{formatCents(balance)}</strong>. Pick how you'd like to be paid — we send it manually within a few business days.
                    </p>
                    <div className="affiliate-modal-method-row">
                        {methods.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                className={`affiliate-modal-method${cashOutMethod === m.id ? ' affiliate-modal-method-selected' : ''}`}
                                onClick={() => this.setState({ cashOutMethod: m.id })}
                                disabled={isSubmittingCashOut}
                                aria-pressed={cashOutMethod === m.id}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                    <label className="affiliate-modal-input-label">
                        Your {activeMethod.label} handle
                        <input
                            type="text"
                            className="affiliate-modal-input"
                            placeholder={activeMethod.hint}
                            value={cashOutHandle}
                            onChange={(e) => this.setState({ cashOutHandle: e.target.value })}
                            disabled={isSubmittingCashOut}
                            autoFocus
                        />
                    </label>
                    {cashOutError && <p className="affiliate-modal-error">{cashOutError}</p>}
                    <div className="affiliate-modal-actions">
                        <button
                            type="submit"
                            className="affiliate-modal-yes"
                            disabled={isSubmittingCashOut}
                        >
                            {isSubmittingCashOut ? 'Submitting…' : 'Submit request'}
                        </button>
                        <button
                            type="button"
                            className="affiliate-modal-no"
                            onClick={this.closeCashOutModal}
                            disabled={isSubmittingCashOut}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    renderReferralStatus(ref) {
        if (ref.current_status === 'cancelled') {
            return <span className="affiliate-row-pill affiliate-row-pill-cancelled">Cancelled</span>;
        }
        if (ref.signup_kind === 'free') {
            return <span className="affiliate-row-pill affiliate-row-pill-free">Free signup</span>;
        }
        const period = ref.billing_period === 'yearly' ? 'Yearly' : 'Monthly';
        return <span className="affiliate-row-pill affiliate-row-pill-active">{period}</span>;
    }

    renderReferralsList(refs) {
        if (!refs || refs.length === 0) {
            return <p className="affiliate-empty">No referrals yet — share your link to start earning.</p>;
        }
        return (
            <ul className="affiliate-list">
                {refs.map((ref) => (
                    <li key={ref.affiliate_referral_index} className="affiliate-list-row">
                        <div className="affiliate-list-row-main">
                            <span className="affiliate-list-row-name" style={{fontFamily: "'Chelsea Market', cursive"}}>ref_{ref.affiliate_referral_index}</span>
                            {this.renderReferralStatus(ref)}
                        </div>
                        <div className="affiliate-list-row-meta">
                            <span className="affiliate-list-row-date">
                                {ref.first_paid_at
                                    ? `Paid since ${formatDate(ref.first_paid_at)}`
                                    : `Signed up ${formatDate(ref.signup_at)} · no paid sub yet`}
                            </span>
                            <span className="affiliate-list-row-amount" style={{fontFamily: "'Chelsea Market', cursive"}}>
                                {formatCents(ref.total_earned_cents)}
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    renderPayoutsList(payouts) {
        if (!payouts || payouts.length === 0) {
            return <p className="affiliate-empty">No payouts yet.</p>;
        }
        return (
            <ul className="affiliate-list">
                {payouts.map((p) => (
                    <li key={p.id} className="affiliate-list-row">
                        <div className="affiliate-list-row-main">
                            <span className="affiliate-list-row-name" style={{fontFamily: "'Chelsea Market', cursive"}}>{formatDate(p.paid_at)}</span>
                            <span className="affiliate-row-pill affiliate-row-pill-paid">Paid</span>
                        </div>
                        <div className="affiliate-list-row-meta">
                            <span className="affiliate-list-row-date">
                                {p.payout_method ? `${p.payout_method.charAt(0).toUpperCase() + p.payout_method.slice(1)}${p.payout_reference ? ` · ${p.payout_reference}` : ''}` : '—'}
                            </span>
                            <span className="affiliate-list-row-amount" style={{fontFamily: "'Chelsea Market', cursive"}}>
                                {formatCents(p.amount_cents)}
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    renderBody() {
        const { loading, error, data } = this.state;
        if (loading) {
            return (
                <div className="affiliate-loading" role="status" aria-live="polite">
                    <div className="loading-spinner"></div>
                </div>
            );
        }
        if (error) {
            return <p className="affiliate-error">{error}</p>;
        }
        if (!data) {
            return <p className="affiliate-error">Affiliate details unavailable.</p>;
        }
        return (
            <>
                {this.renderShareCard(data.affiliate)}
                {this.renderKpiGrid(data.kpis)}
                {this.renderCashOutSection(data)}
                <section className="affiliate-section">
                    <h3 className="affiliate-section-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}>
                        <u>Your Referrals</u>
                    </h3>
                    {this.renderReferralsList(data.recent_referrals)}
                </section>
                <section className="affiliate-section">
                    <h3 className="affiliate-section-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}>
                        <u>Payout History</u>
                    </h3>
                    {this.renderPayoutsList(data.payout_history)}
                </section>
                <p className="affiliate-footnote">
                    Earnings update after each successful billing cycle. Payouts are processed manually — reach out at <a href="mailto:hello@animates.app" className="affiliate-footnote-link">hello@animates.app</a> with questions.
                </p>
            </>
        );
    }

    render() {
        if (this.state.forbidden) {
            return <Navigate to="/home" replace />;
        }
        return (
            <AuthGate>
                <div className="settings-page">
                    <AccountHeader />
                    <div className="settings-page-content affiliate-content">
                        <Link to="/home" className="settings-back-link">Back</Link>
                        <h2 className="settings-page-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}>
                            <u>Affiliate Dashboard</u>
                        </h2>
                        {this.renderBody()}
                    </div>
                    {this.state.showCashOutModal && this.renderCashOutModal()}
                </div>
            </AuthGate>
        );
    }
}

export default AffiliateDashboard;
